import { useEffect, useState } from 'react';
import { Link, NavLink, useParams, Navigate } from 'react-router-dom';
import { Crown, Users, Sparkles, Calendar, Newspaper, ImageIcon as GalleryIcon, BookOpen, Mail, MapPin, Phone, ChevronRight, ArrowRight, Star, Menu, X } from 'lucide-react';
import api from '@/lib/api';
import { useSiteData, usePageContent } from '@/context/SiteDataContext';
import { toast } from 'sonner';
import GalleryRows from '@/components/GalleryRows';
import AdminHint from '@/components/editable/AdminHint';

const WOMENS_DEPT_ID = 'women';

// -----------------------------------------------------------------------------
// Layout wrapper — provides the mini-site sub-navigation and themed hero band.
// -----------------------------------------------------------------------------

const NAV_LINKS_FALLBACK = [
  { to: '/womens', label: 'Home', end: true },
  { to: '/womens/about', label: 'About Us' },
  { to: '/womens/leadership', label: 'Leadership' },
  { to: '/womens/branches', label: 'Branches' },
  { to: '/womens/events', label: 'Events' },
  { to: '/womens/news', label: 'News & Announcements' },
  { to: '/womens/gallery', label: 'Gallery' },
  { to: '/womens/resources', label: 'Resources' },
  { to: '/womens/contact', label: 'Contact' },
];

function useWomensNav() {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get('/site/womens-nav').then(r => setItems(r.data || [])).catch(() => setItems([]));
  }, []);
  if (items === null) return NAV_LINKS_FALLBACK;
  if (!items.length) return NAV_LINKS_FALLBACK;
  return items
    .filter(i => i.visible !== false)
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
    .map(i => ({ to: i.href, label: i.label, end: i.href === '/womens' }));
}

function useWomensContext() {
  const { departments, leaders, settings } = useSiteData();
  const c = usePageContent('womens-home');
  const dept = (departments || []).find(d => d.id === WOMENS_DEPT_ID) || {};
  // Prefer explicit `assignments` (new model), fall back to legacy department_id linkage.
  const leadersById = Object.fromEntries((leaders || []).map(l => [l.id, l]));
  const assignments = (dept.assignments || []).slice().sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  const assigned = assignments.map(a => ({ ...leadersById[a.leader_id], _position: a.position })).filter(x => x && x.id);
  const legacy = (leaders || []).filter(l => l.department_id === WOMENS_DEPT_ID).map(l => ({ ...l, _position: l.department_role || l.role }));
  const cabinet = assigned.length ? assigned : legacy;
  const supervisor = cabinet.find(l => /supervisor|president/i.test(l._position || l.role || '')) || cabinet[0] || null;
  return { c, dept, cabinet, supervisor, settings: settings || {} };
}

export function WomensLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { c, dept, settings } = useWomensContext();
  const NAV_LINKS = useWomensNav();
  const logo = c.womens_logo || settings.womens_logo || dept.image || settings.logo_url;
  const motto = c.motto || settings.womens_motto || dept.tagline || 'Called. Chosen. Consecrated.';
  const name = c.womens_name || dept.name || "Women's Department";
  // Apply Women's-scoped theme colors as CSS variables local to the mini-site.
  const themeStyle = {
    '--womens-primary': settings.womens_primary_color || '#5A2882',
    '--womens-accent': settings.womens_accent_color || '#E4C169',
  };
  return (
    <div className="womens-namespace" style={themeStyle}>
      {/* Themed sub-header (below main jurisdiction header) */}
      <div className="relative bg-gradient-to-r from-royal via-royal-dark to-royal border-b-2 border-gold">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Link to="/womens" data-testid="womens-brand" className="flex items-center gap-3 min-w-0">
            {logo ? (
              <img src={logo} alt="" className="w-11 h-11 rounded-full ring-2 ring-gold bg-white object-cover shrink-0" />
            ) : (
              <div className="w-11 h-11 rounded-full ring-2 ring-gold bg-white/10 flex items-center justify-center shrink-0">
                <Crown className="w-5 h-5 text-gold" />
              </div>
            )}
            <div className="min-w-0">
              <div className="text-white font-serif text-lg sm:text-xl font-bold truncate">{name}</div>
              <div className="text-gold text-[10px] sm:text-xs uppercase tracking-widest truncate">{motto}</div>
            </div>
          </Link>
          <div className="ml-auto lg:hidden">
            <button data-testid="womens-nav-toggle" onClick={() => setOpen(v => !v)} className="text-white p-2 rounded hover:bg-white/10">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map(l => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                data-testid={`womens-nav-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-wider px-3 py-2 rounded-full transition ${
                    isActive
                      ? 'bg-gold text-royal-dark font-bold'
                      : 'text-white/85 hover:text-gold hover:bg-white/5'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>
        </div>
        {open && (
          <div className="lg:hidden bg-royal-dark border-t border-gold/40">
            <div className="max-w-7xl mx-auto px-4 py-2 flex flex-col">
              {NAV_LINKS.map(l => (
                <NavLink
                  key={l.to} to={l.to} end={l.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `py-2 text-sm border-b border-white/5 last:border-0 ${isActive ? 'text-gold font-bold' : 'text-white/85'}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Section building blocks (reused across sub-pages)
// -----------------------------------------------------------------------------

const PageHeader = ({ overline, title, blurb, image }) => (
  <section className="relative py-20 overflow-hidden">
    <div className="absolute inset-0">
      {image && <img src={image} alt="" className="w-full h-full object-cover opacity-40" />}
      <div className="absolute inset-0 bg-gradient-to-br from-royal via-royal-dark to-royal" />
    </div>
    <div className="relative max-w-6xl mx-auto px-6 text-white">
      {overline && <div className="overline text-gold mb-3">{overline}</div>}
      <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold gold-underline">{title}</h1>
      {blurb && <p className="mt-4 text-white/85 text-lg max-w-3xl">{blurb}</p>}
    </div>
  </section>
);

const Empty = ({ children }) => (
  <AdminHint className="bg-white border rounded-lg p-8 text-center text-charcoal-muted text-sm">{children}</AdminHint>
);

// -----------------------------------------------------------------------------
// HOME
// -----------------------------------------------------------------------------

export function WomensHome() {
  const { c, dept, cabinet, supervisor, settings } = useWomensContext();
  const [branches, setBranches] = useState([]);
  useEffect(() => {
    api.get('/site/womens-branches').then(r => setBranches(r.data || [])).catch(() => {});
  }, []);
  const heroImg = c.womens_hero_image || dept.image;
  const heroVideo = c.hero_video_url;

  return (
    <div>
      {/* Hero — supports video background (overrides image) */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          {heroVideo ? (
            <video autoPlay muted loop playsInline data-testid="womens-hero-video" className="w-full h-full object-cover">
              <source src={heroVideo} type="video/mp4" />
            </video>
          ) : (
            heroImg && <img src={heroImg} alt="" className="w-full h-full object-cover" />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-royal-dark/85 via-royal/70 to-royal-dark/90" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-20 text-white">
          <div className="overline text-gold mb-4">{c.hero_overline || 'Wisconsin First Ecclesiastical Jurisdiction'}</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl">{c.womens_name || dept.name || "Women's Department"}</h1>
          <p className="mt-4 text-gold text-xl italic font-serif">"{c.motto || settings.womens_motto || dept.tagline || 'Called. Chosen. Consecrated.'}"</p>
          {c.hero_intro && <p className="mt-6 max-w-2xl text-white/85 text-lg leading-relaxed">{c.hero_intro}</p>}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/womens/leadership" data-testid="womens-hero-leadership" className="btn-gold">Meet Our Leadership <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/womens/branches" className="btn-outline-white">Explore Branches</Link>
          </div>
        </div>
      </section>

      {/* Supervisor welcome */}
      {supervisor && (
        <section className="relative bg-white/90 backdrop-blur-md py-16">
          <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-4">
              <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-2xl ring-4 ring-gold/40" data-testid="womens-supervisor-photo">
                {(c.supervisor_photo || supervisor.picture || supervisor.photo)
                  ? <img src={c.supervisor_photo || supervisor.picture || supervisor.photo} alt={supervisor.name} className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-royal/10 flex items-center justify-center"><Users className="w-12 h-12 text-royal" /></div>}
              </div>
            </div>
            <div className="md:col-span-8">
              <div className="overline mb-3">A Message From Your Supervisor</div>
              <h2 className="font-serif text-3xl lg:text-4xl text-royal font-bold">{supervisor.name}</h2>
              <div className="text-gold font-semibold uppercase tracking-wider text-sm mt-1">{supervisor.role || 'Jurisdictional Supervisor of Women'}</div>
              <p className="mt-6 text-charcoal-muted text-lg leading-relaxed whitespace-pre-wrap">
                {c.supervisor_welcome ||
                  `Greetings in the mighty name of our Lord and Savior Jesus Christ. It is my honor to serve as your Supervisor of Women. The Women's Department stands united in prayer, purpose, and Kingdom impact — advancing the Gospel through worship, missions, discipleship, and love in action. We invite every woman of the jurisdiction to walk with us in her divine calling.`}
              </p>
              <Link to="/womens/leadership" className="mt-6 inline-flex items-center gap-1 text-royal font-semibold hover:text-gold">Meet the leadership <ChevronRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </section>
      )}

      {/* Branches quick links — kept because it links to the /womens/branches page */}
      <section className="relative bg-royal-dark/85 backdrop-blur-md text-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="overline text-gold mb-2">Ministry Branches</div>
              <h3 className="font-serif text-2xl lg:text-3xl font-bold">Every Woman. Every Season. Every Gift.</h3>
            </div>
            <Link to="/womens/branches" className="text-gold text-sm hover:text-white">All branches <ChevronRight className="inline w-4 h-4" /></Link>
          </div>
          {branches.length === 0 ? (
            <Empty>No branches configured yet. Admins can add them from Admin ▸ Women's Ministry ▸ Branches.</Empty>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {branches.slice(0, 8).map(b => (
                <Link key={b.id} to={`/womens/branches/${b.id}`} data-testid={`womens-branch-card-${b.id}`} className="group bg-white/5 hover:bg-white/10 border border-white/10 hover:border-gold rounded-lg overflow-hidden transition">
                  <div className="aspect-[4/3] bg-royal-dark overflow-hidden">
                    {(b.image || b.logo) && <img src={b.image || b.logo} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                  </div>
                  <div className="p-4">
                    <div className="font-serif text-lg font-bold group-hover:text-gold">{b.name}</div>
                    {b.leader_name && <div className="text-xs text-white/70 mt-1">{b.leader_name}</div>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Women's Gallery rows (mixed-portrait layouts) — renders when admin has added at least one row */}
      <GalleryRows scope="womens" wrapClass="relative bg-white/90 backdrop-blur-md py-16" innerClass="max-w-6xl mx-auto px-6">
        <div className="overline mb-2">Moments</div>
        <h3 className="font-serif text-2xl text-royal font-bold gold-underline mb-8">In the Life of the Department</h3>
      </GalleryRows>

      {/* Contact CTA */}
      <section className="relative bg-[#F8F9FA]/90 backdrop-blur-md py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Sparkles className="w-8 h-8 text-gold mx-auto mb-3" />
          <h3 className="font-serif text-3xl text-royal font-bold">Connect With Us</h3>
          <p className="mt-3 text-charcoal-muted">Have a question, prayer request, or want to serve in one of our branches? We'd love to hear from you.</p>
          <div className="mt-6 flex justify-center gap-3 flex-wrap">
            <Link to="/womens/contact" className="btn-royal">Contact the Department</Link>
            <Link to="/prayer" className="btn-outline-royal">Submit a Prayer Request</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// ABOUT
// -----------------------------------------------------------------------------

export function WomensAbout() {
  const { c, dept, settings } = useWomensContext();
  const heroImg = c.about_hero_image || c.womens_hero_image || dept.image;
  const heroVideo = c.about_hero_video_url;
  return (
    <div>
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          {heroVideo ? (
            <video autoPlay muted loop playsInline data-testid="womens-about-hero-video" className="w-full h-full object-cover">
              <source src={heroVideo} type="video/mp4" />
            </video>
          ) : (
            heroImg && <img src={heroImg} alt="" className="w-full h-full object-cover opacity-40" />
          )}
          <div className="absolute inset-0 bg-gradient-to-br from-royal via-royal-dark to-royal" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 text-white">
          <div className="overline text-gold mb-3">Who We Are</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold gold-underline">About the Women's Department</h1>
          <p className="mt-4 text-white/85 text-lg max-w-3xl">
            {c.about_blurb || dept.tagline || 'Advancing the Kingdom through the daughters of the jurisdiction.'}
          </p>
        </div>
      </section>
      <section className="bg-white/90 backdrop-blur-md py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-6 text-charcoal-muted text-lg leading-relaxed">
          <p className="whitespace-pre-wrap">
            {c.about_body || dept.mission || `The Women's Department of the Wisconsin First Ecclesiastical Jurisdiction serves as the collective voice, ministry, and mission arm of the women of our fellowship. Through worship, prayer, mentorship, evangelism, and hands-on service, we equip women in every season of life to walk in their divine calling and impact their communities for Christ.`}
          </p>
          {c.about_mission && (<div className="bg-royal/5 border-l-4 border-gold p-6 rounded"><div className="overline mb-1">Our Mission</div><p className="text-royal font-serif text-xl">{c.about_mission}</p></div>)}
          {c.about_vision && (<div className="bg-royal/5 border-l-4 border-gold p-6 rounded"><div className="overline mb-1">Our Vision</div><p className="text-royal font-serif text-xl">{c.about_vision}</p></div>)}
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// LEADERSHIP
// -----------------------------------------------------------------------------

export function WomensLeadership() {
  const { cabinet } = useWomensContext();
  const [selected, setSelected] = useState(null);
  return (
    <div>
      <PageHeader overline="Leadership" title="Cabinet & Officers" blurb="The women called to shepherd, teach, and lead the daughters of Wisconsin First." />
      <section className="bg-white/90 backdrop-blur-md py-16">
        <div className="max-w-6xl mx-auto px-6">
          {cabinet.length === 0 && <Empty>No leaders assigned yet. Admins can tag leaders with department "Women's Department" from Admin ▸ Leaders.</Empty>}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cabinet.map(l => (
              <button key={l.id} data-testid={`womens-leader-${l.id}`} onClick={() => setSelected(l)} className="card-tactile overflow-hidden text-left group">
                <div className="aspect-[3/4] overflow-hidden bg-royal/5">
                  {(l.picture || l.photo) && <img src={l.picture || l.photo} alt={l.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    style={{ objectPosition: l.photoPosition || 'center top' }} />}
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-gold">{l._position || l.section || 'Cabinet'}</div>
                  <div className="font-serif text-xl text-royal font-bold mt-1">{l.name}</div>
                  <div className="text-sm text-charcoal-muted mt-1">{l.role}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-white max-w-2xl w-full rounded-lg overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="grid md:grid-cols-5">
              <div className="md:col-span-2 aspect-[3/4] md:aspect-auto bg-royal/5">
                {selected.picture && <img src={selected.picture} alt={selected.name} className="w-full h-full object-cover" />}
              </div>
              <div className="md:col-span-3 p-6">
                <div className="text-[10px] uppercase tracking-widest text-gold">{selected.section}</div>
                <div className="font-serif text-2xl text-royal font-bold mt-1">{selected.name}</div>
                <div className="text-sm text-charcoal-muted">{selected.role}</div>
                {selected.bio && <p className="mt-4 text-sm text-charcoal-muted leading-relaxed whitespace-pre-wrap">{selected.bio}</p>}
                {selected.contact_email && <div className="mt-4 text-sm"><Mail className="w-3 h-3 inline mr-1" /> <a href={`mailto:${selected.contact_email}`} className="text-royal underline">{selected.contact_email}</a></div>}
                {selected.contact_phone && <div className="text-sm"><Phone className="w-3 h-3 inline mr-1" /> {selected.contact_phone}</div>}
                <button data-testid="womens-leader-close" onClick={() => setSelected(null)} className="mt-6 text-xs uppercase tracking-widest text-royal hover:text-gold">Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// BRANCHES (list + detail)
// -----------------------------------------------------------------------------

export function WomensBranches() {
  const [branches, setBranches] = useState([]);
  useEffect(() => { api.get('/site/womens-branches').then(r => setBranches(r.data || [])).catch(() => {}); }, []);
  return (
    <div>
      <PageHeader overline="Branches" title="Ministry Branches & Auxiliaries" blurb="Each branch equips women with a distinct focus — from prayer and purity to missions and mentorship." />
      <section className="bg-white/90 backdrop-blur-md py-16">
        <div className="max-w-6xl mx-auto px-6">
          {branches.length === 0 && <Empty>No branches configured yet.</Empty>}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {branches.map(b => (
              <Link key={b.id} to={`/womens/branches/${b.id}`} data-testid={`womens-branch-tile-${b.id}`} className="card-tactile overflow-hidden group">
                <div className="aspect-[16/10] bg-royal/5 overflow-hidden">
                  {(b.image || b.logo) && <img src={b.image || b.logo} alt={b.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-xl text-royal font-bold group-hover:text-gold">{b.name}</h3>
                  {b.tagline && <div className="text-[10px] text-gold uppercase tracking-widest mt-1">{b.tagline}</div>}
                  {b.description && <p className="text-sm text-charcoal-muted mt-3 line-clamp-3">{b.description}</p>}
                  <div className="mt-4 text-sm text-royal font-semibold flex items-center gap-1">Learn more <ChevronRight className="w-4 h-4" /></div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function WomensBranchDetail() {
  const { id } = useParams();
  const [branch, setBranch] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [events, setEvents] = useState([]);
  const [newsList, setNewsList] = useState([]);
  const { gallery = [] } = useSiteData();
  useEffect(() => {
    api.get(`/site/womens-branches/${id}`).then(r => setBranch(r.data)).catch(() => setNotFound(true));
    api.get('/events').then(r => setEvents(r.data || [])).catch(() => {});
    api.get('/news').then(r => setNewsList(r.data || [])).catch(() => {});
  }, [id]);
  if (notFound) return <div className="py-24 text-center">Branch not found. <Link to="/womens/branches" className="text-royal underline">Back</Link></div>;
  if (!branch) return <div className="py-24 text-center">Loading…</div>;

  const key = (branch.slug || branch.name || '').toLowerCase();
  const relEvents = events.filter(e => (e.department || '').toLowerCase().includes(key)).slice(0, 4);
  const relNews = newsList.filter(n => ((n.category || '') + ' ' + (n.department || '')).toLowerCase().includes(key)).slice(0, 4);
  const photos = (gallery || []).filter(g => ((g.tag || '') + ' ' + (g.caption || '')).toLowerCase().includes(key)).slice(0, 6);

  return (
    <div>
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0">
          {branch.image && <img src={branch.image} alt="" className="w-full h-full object-cover opacity-40" />}
          <div className="absolute inset-0 bg-gradient-to-br from-royal via-royal-dark to-royal" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6 text-white flex items-center gap-6 flex-wrap">
          {branch.logo && <img src={branch.logo} alt="" className="w-24 h-24 rounded-full ring-4 ring-gold bg-white object-cover" />}
          <div className="min-w-0">
            <div className="overline text-gold mb-2">Women's Department Branch</div>
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold">{branch.name}</h1>
            {branch.tagline && <div className="mt-2 text-gold italic font-serif">"{branch.tagline}"</div>}
          </div>
        </div>
      </section>

      <section className="bg-white/90 backdrop-blur-md py-14">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-8 space-y-6">
            {branch.description && (<div><div className="overline mb-2">About</div><p className="text-charcoal-muted leading-relaxed whitespace-pre-wrap">{branch.description}</p></div>)}
            {branch.mission && (<div className="bg-royal/5 border-l-4 border-gold p-5 rounded"><div className="overline mb-1">Mission</div><div className="text-royal font-serif text-lg">{branch.mission}</div></div>)}
          </div>
          <div className="md:col-span-4">
            <div className="bg-royal text-white p-6 rounded-lg">
              <div className="overline text-gold mb-3">Branch Leader</div>
              <div className="flex items-center gap-4">
                {branch.leader_photo
                  ? <img src={branch.leader_photo} alt={branch.leader_name} className="w-16 h-16 rounded-full ring-2 ring-gold object-cover" />
                  : <div className="w-16 h-16 rounded-full ring-2 ring-gold bg-white/10 flex items-center justify-center"><Users className="w-6 h-6 text-gold" /></div>}
                <div className="min-w-0">
                  <div className="font-serif text-lg font-bold truncate">{branch.leader_name || 'TBD'}</div>
                  {branch.leader_title && <div className="text-xs text-gold uppercase tracking-widest">{branch.leader_title}</div>}
                </div>
              </div>
              <div className="mt-4 space-y-1 text-xs text-white/85">
                {branch.email && <div><Mail className="inline w-3 h-3 mr-1" /> <a href={`mailto:${branch.email}`} className="hover:text-gold underline">{branch.email}</a></div>}
                {branch.phone && <div><Phone className="inline w-3 h-3 mr-1" /> {branch.phone}</div>}
                {branch.address && <div><MapPin className="inline w-3 h-3 mr-1" /> {branch.address}</div>}
              </div>
            </div>
          </div>
        </div>
      </section>

      {(relEvents.length > 0 || relNews.length > 0) && (
        <section className="bg-[#F8F9FA]/90 backdrop-blur-md py-14">
          <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-8">
            <div>
              <div className="overline mb-2">Upcoming</div>
              <h3 className="font-serif text-2xl text-royal font-bold mb-4">Events</h3>
              {relEvents.length === 0 ? <Empty>No events yet.</Empty> : relEvents.map(e => (
                <div key={e.id} className="card-tactile p-4 mb-2">
                  <div className="font-serif text-royal font-bold">{e.title}</div>
                  <div className="text-xs text-charcoal-muted">{e.date} · {e.location}</div>
                </div>
              ))}
            </div>
            <div>
              <div className="overline mb-2">Latest</div>
              <h3 className="font-serif text-2xl text-royal font-bold mb-4">Announcements</h3>
              {relNews.length === 0 ? <Empty>No announcements yet.</Empty> : relNews.map(n => (
                <Link key={n.id} to={`/news/${n.id}`} className="block card-tactile p-4 mb-2">
                  <div className="font-serif text-royal font-bold">{n.title}</div>
                  {n.excerpt && <div className="text-xs text-charcoal-muted mt-1 line-clamp-2">{n.excerpt}</div>}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {photos.length > 0 && (
        <section className="bg-white/90 backdrop-blur-md py-14">
          <div className="max-w-5xl mx-auto px-6">
            <div className="overline mb-2">Gallery</div>
            <h3 className="font-serif text-2xl text-royal font-bold mb-4">Photos</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {photos.map(p => (
                <div key={p.id} className="aspect-[4/3] overflow-hidden rounded"><img src={p.url} alt={p.caption || ''} className="w-full h-full object-cover" /></div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-royal-dark/85 text-white py-10">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between flex-wrap gap-4">
          <Link to="/womens/branches" className="text-gold hover:text-white">‹ All branches</Link>
          <Link to="/womens/contact" className="btn-outline-white">Contact Us</Link>
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// EVENTS
// -----------------------------------------------------------------------------

export function WomensEvents() {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get('/events').then(r => setEvents((r.data || []).filter(e => /women/i.test(e.department || '')))).catch(() => {});
  }, []);
  return (
    <div>
      <PageHeader overline="Calendar" title="Women's Department Events" blurb="Conferences, retreats, prayer summits, and gatherings from across the jurisdiction." />
      <section className="bg-white/90 backdrop-blur-md py-14">
        <div className="max-w-5xl mx-auto px-6 space-y-4">
          {events.length === 0 && <Empty>No events yet. Once admins tag events with "Women's Department" they'll appear here.</Empty>}
          {events.map(e => (
            <div key={e.id} data-testid={`womens-events-item-${e.id}`} className="card-tactile p-5 flex gap-5 items-start">
              <div className="text-center bg-royal text-white rounded-lg p-3 min-w-[72px]">
                <div className="text-xs uppercase">{(e.date || '').slice(5, 7)}</div>
                <div className="text-2xl font-bold">{(e.date || '').slice(8, 10)}</div>
                <div className="text-[10px] text-gold uppercase mt-1">{(e.date || '').slice(0, 4)}</div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-serif text-xl text-royal font-bold">{e.title}</h3>
                <div className="text-xs text-charcoal-muted mt-1 flex items-center gap-3 flex-wrap">
                  {e.time && <span>{e.time}</span>}
                  {e.location && <span><MapPin className="w-3 h-3 inline mr-1" />{e.location}</span>}
                </div>
                {e.description && <p className="mt-3 text-sm text-charcoal-muted line-clamp-3">{e.description}</p>}
                {e.registration_url && <a href={e.registration_url} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-royal font-semibold hover:text-gold">Register <ChevronRight className="w-4 h-4" /></a>}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// NEWS
// -----------------------------------------------------------------------------

export function WomensNews() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get('/news').then(r => setItems((r.data || []).filter(n => /women/i.test((n.category || '') + ' ' + (n.department || ''))))).catch(() => {});
  }, []);
  return (
    <div>
      <PageHeader overline="Newsroom" title="News & Announcements" blurb="Updates, prayer alerts, and Kingdom stories from the daughters of Wisconsin First." />
      <section className="bg-white/90 backdrop-blur-md py-14">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-2 gap-6">
          {items.length === 0 && <div className="md:col-span-2"><Empty>No news yet. Tag news items with "Women's Department" in the admin to feature them here.</Empty></div>}
          {items.map(n => (
            <Link key={n.id} to={`/news/${n.id}`} data-testid={`womens-news-item-${n.id}`} className="card-tactile overflow-hidden group">
              {n.image && <div className="aspect-[16/10] overflow-hidden"><img src={n.image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>}
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-gold">{n.category || 'News'}</div>
                <div className="font-serif text-xl text-royal font-bold mt-1 group-hover:text-gold">{n.title}</div>
                {n.excerpt && <p className="text-sm text-charcoal-muted mt-2 line-clamp-3">{n.excerpt}</p>}
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// GALLERY
// -----------------------------------------------------------------------------

export function WomensGallery() {
  const { gallery = [] } = useSiteData();
  const filtered = (gallery || []).filter(g => /women/i.test((g.tag || g.caption || '')));
  const items = filtered.length ? filtered : gallery;
  return (
    <div>
      <PageHeader overline="Gallery" title="Ministry in Motion" blurb="A visual record of our conferences, retreats, and service across Wisconsin." />
      <section className="bg-white/90 backdrop-blur-md py-14">
        <div className="max-w-6xl mx-auto px-6">
          {items.length === 0 && <Empty>No photos yet.</Empty>}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {items.map(p => (
              <div key={p.id} className="aspect-square overflow-hidden rounded-lg group cursor-pointer">
                <img src={p.url} alt={p.caption || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// RESOURCES
// -----------------------------------------------------------------------------

export function WomensResources() {
  const c = usePageContent('womens-resources');
  const resources = c.resources || [
    { title: "Women's Department Handbook", href: '#', kind: 'PDF' },
    { title: 'Weekly Prayer Guide', href: '#', kind: 'PDF' },
    { title: 'Devotional Reading Plan', href: '#', kind: 'Document' },
    { title: 'Financial Stewardship Toolkit', href: '#', kind: 'Toolkit' },
  ];
  return (
    <div>
      <PageHeader overline="Equip" title="Resources" blurb="Study guides, prayer plans, and tools for daughters at every level of ministry." />
      <section className="bg-white/90 backdrop-blur-md py-14">
        <div className="max-w-4xl mx-auto px-6 grid md:grid-cols-2 gap-4">
          {resources.map((r, i) => (
            <a key={`${r.title}-${i}`} href={r.href} target="_blank" rel="noopener noreferrer" className="card-tactile p-5 flex items-start gap-4 group">
              <div className="w-12 h-12 rounded bg-royal/10 flex items-center justify-center shrink-0"><BookOpen className="w-6 h-6 text-royal" /></div>
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-gold">{r.kind || 'Resource'}</div>
                <div className="font-serif text-lg text-royal font-bold group-hover:text-gold">{r.title}</div>
                {r.description && <div className="text-sm text-charcoal-muted mt-1">{r.description}</div>}
              </div>
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// CONTACT
// -----------------------------------------------------------------------------

export function WomensContact() {
  const { supervisor, settings, c } = useWomensContext();
  const email = c.contact_email || settings.womens_email || 'women@wifirstjurisdiction.org';
  const phone = c.contact_phone || settings.womens_phone || settings.phone;
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/contact', { ...form, department: "Women's Department" });
      toast.success('Message sent — we will be in touch shortly.');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch { toast.error('Send failed'); } finally { setBusy(false); }
  };
  return (
    <div>
      <PageHeader overline="Get In Touch" title="Contact the Women's Department" blurb="Reach the office of the Supervisor for prayer, ministry, or general inquiries." />
      <section className="bg-white/90 backdrop-blur-md py-14">
        <div className="max-w-5xl mx-auto px-6 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5 space-y-6">
            <div className="bg-royal text-white p-6 rounded-lg">
              <div className="overline text-gold mb-3">Office of the Supervisor</div>
              {supervisor && (<>
                <div className="font-serif text-2xl font-bold">{supervisor.name}</div>
                <div className="text-gold text-xs uppercase tracking-widest">{supervisor.role || 'Jurisdictional Supervisor of Women'}</div>
              </>)}
              <div className="mt-5 space-y-2 text-sm">
                {email && <div><Mail className="inline w-4 h-4 mr-2 text-gold" /><a href={`mailto:${email}`} className="hover:text-gold underline">{email}</a></div>}
                {phone && <div><Phone className="inline w-4 h-4 mr-2 text-gold" /> {phone}</div>}
                {settings.address_line1 && <div><MapPin className="inline w-4 h-4 mr-2 text-gold" /> {settings.address_line1} {settings.address_line2}</div>}
              </div>
            </div>
            <div className="text-sm text-charcoal-muted">
              For prayer requests, please also visit <Link to="/prayer" className="text-royal underline">/prayer</Link>.
            </div>
          </div>
          <form onSubmit={submit} className="md:col-span-7 bg-white border rounded-lg p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="block"><span className="text-sm font-semibold text-royal">Name</span><input required data-testid="womens-contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded" /></label>
              <label className="block"><span className="text-sm font-semibold text-royal">Email</span><input required type="email" data-testid="womens-contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded" /></label>
            </div>
            <label className="block"><span className="text-sm font-semibold text-royal">Subject</span><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded" /></label>
            <label className="block"><span className="text-sm font-semibold text-royal">Message</span><textarea required rows="5" data-testid="womens-contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded" /></label>
            <button type="submit" disabled={busy} data-testid="womens-contact-submit" className="btn-royal">{busy ? 'Sending…' : 'Send Message'}</button>
          </form>
        </div>
      </section>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Redirect helper — DepartmentDetail can route "women" here
// -----------------------------------------------------------------------------
export function WomensDeptRedirect() { return <Navigate to="/womens" replace />; }
